<template>
  <div>
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center">
        <div class="color-gray-80 text-base font-semibold" data-testid="date-condition-group">
          {{ $t('filter.groups.date') }}
        </div>
        <ui-tag v-if="getActiveFilterCount(dateGroupFilter)" rounded theme="brand-light" class="ml-3">
          {{ $t('universal.applied', getActiveFilterCount(dateGroupFilter)) }}
        </ui-tag>
      </div>
    </div>
    <filter-group
      :added-filters="getFilterGroupItems(dateGroupFilter)"
      :filter-options="filterOptions.filter(dateGroupFilter)"
      :error="!getActiveFilterCount(dateGroupFilter)"
      @filter-adding-started="filterAddingStarted($event)"
      @filter-applied="filterApplied"
      @add-similar-filter="addSimilarFilter"
      @remove="removeFilter($event)"
    />
    <div v-if="!getActiveFilterCount(dateGroupFilter)" class="text-red-60 mt-2.5 text-xs font-medium">
      {{ $t('filter.date_condition_warning') }}
    </div>

    <div class="bg-gray-20 mb-8 mt-[36px] h-0.5" />

    <div v-for="group in groupsWithToggle" :key="group.name">
      <div class="flex items-center justify-between bg-white pb-3" :class="isModal && 'sticky top-0 z-[2]'">
        <div class="flex items-center">
          <div class="color-gray-80 text-base font-semibold">{{ group.name }}</div>
          <ui-tag
            v-if="getActiveFilterCount(getAllGroupsFilter(group.subGroups))"
            rounded
            theme="brand-light"
            class="ml-3"
          >
            {{ $t('universal.applied', getActiveFilterCount(getAllGroupsFilter(group.subGroups))) }}
          </ui-tag>
        </div>
        <div class="flex min-h-8 items-center gap-4">
          <search-input
            v-if="group.showAllFilters.value"
            v-model="group.searchValue.value"
            small
            :placeholder="$t('filter.groups.search_placeholder')"
          />
          <ui-pill-toggle v-model="group.showAllFilters.value">
            <template #label>
              <span class="text-gray-80 text-sm font-medium">
                {{ $t('filter.groups.show_all') }}
              </span>
            </template>
          </ui-pill-toggle>
        </div>
      </div>
      <template v-if="group.showAllFilters.value">
        <div v-for="(subGroup, i) in group.subGroups" :key="subGroup.name">
          <div
            v-if="
              (group.subGroups.length > 1 && filterOptions.filter(subGroup.filterFn).length) ||
              getFilterGroupItems(subGroup.filterFn).length
            "
            class="color-gray-70 flex items-center gap-2 text-xxs font-bold uppercase"
            :class="i > 0 ? 'mt-8' : undefined"
          >
            {{ subGroup.name }}
            <tippy v-if="subGroup?.tooltipContent" ref="tippyRef" :content="subGroup.tooltipContent">
              <info-icon class="icon-m text-gray-60" />
            </tippy>
          </div>
          <filter-group
            v-if="
              filterOptions.filter(subGroup.filterFn).length ||
              hasActiveVisibleFilters(subGroup.filterFn) ||
              getFilterGroupItems(subGroup.filterFn).length
            "
            class="mt-2.5"
            :added-filters="getFilterGroupItems(subGroup.filterFn)"
            :filter-options="filterOptions.filter(subGroup.filterFn)"
            :search-filter="group.searchValue.value"
            show-all
            @filter-adding-started="filterAddingStarted($event)"
            @filter-applied="filterApplied"
            @add-similar-filter="addSimilarFilter"
            @remove="removeFilter($event)"
          />
        </div>
      </template>
      <template v-else>
        <filter-group
          :added-filters="getFilterGroupItems(getAllGroupsFilter(group.subGroups))"
          :filter-options="filterOptions.filter(getAllGroupsFilter(group.subGroups))"
          @filter-adding-started="filterAddingStarted($event)"
          @filter-applied="filterApplied"
          @add-similar-filter="addSimilarFilter"
          @remove="removeFilter($event)"
        />
      </template>

      <div class="bg-gray-20 mb-8 mt-[36px] h-0.5" />
    </div>

    <div class="mb-3 flex items-center">
      <div class="color-gray-80 text-base font-semibold">{{ $t('filter.groups.helpdesk') }}</div>
      <ui-tag v-if="getActiveFilterCount(helpdeskGroupFilter)" rounded theme="brand-light" class="ml-3">
        {{ $t('universal.applied', getActiveFilterCount(helpdeskGroupFilter)) }}
      </ui-tag>
    </div>
    <helpdesk-filter-group
      :added-filters="helpdeskFilters"
      :selected-option-names="selectedOptionNames"
      :options="options"
      @filter-adding-started="loadOptionValues"
      @filter-applied="replaceOrAddFilter"
      @add-similar-filter="addSimilarFilter"
      @remove="removeFilter($event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { Tippy } from 'vue-tippy'
import { cloneDeep, debounce, isEqual } from 'lodash-es'

import { UiPillToggle, UiTag } from '@klausapp/ui-kit'
import { InfoIcon } from '@klausapp/ui-kit/icons'
import i18n from '@/i18n'
import {
  type DropdownSelectedValue,
  type AddedFilter,
  type FilterItemEmit,
  type FilterOption,
  type ValueComponentProps,
  FilterValueType,
  type FilterModalType,
} from '@/components/filters/types'

import FilterGroup from '@/components/filters/FilterGroup.vue'
import SearchInput from '@/components/SearchInput.vue'
import { isActiveFilter } from '@/components/filters/utils'

import { translationExists } from '@/i18n/utils'
import { type DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import useFilterOptions from '../../composables/useFilterOptions'
import useVisibleFilters from '../../composables/useVisibleFilters'
import useFilterValues from '../../composables/useFilterValues'

import HelpdeskFilterGroup from './HelpdeskFilterGroup.vue'

import {
  type GroupFilter,
  getConversationFilters,
  dateGroupFilter,
  helpdeskGroupFilter,
  getAllGroupsFilter,
  hasIntelligenceIcon,
  hasBotIcon,
} from './filterGrouping'

import { addLabel, getDescriptionKey, getDescriptionOptions, isDowngradedFilter, forcedFilterOption } from './utils'

const props = defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  customCategoryFields: DefaultFilterOption[]
  intelligentFilterFields: DefaultFilterOption[]
  modelValue: DeconstructedOption[]
  editModal?: boolean
  isModal?: boolean
  reviewerQa?: boolean
  hideAllConversationFilters?: boolean
  modalType?: FilterModalType
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: DeconstructedOption[]): void
  (e: 'disable-selecting-tickets', value: boolean): void
}>()

const { groupsWithToggle } = getConversationFilters(
  !!props.reviewerQa,
  !props.hideAllConversationFilters && !props.editModal,
)

const translateLabels = (values: DeconstructedOption[]) => {
  return values.map((value) => ({
    ...value,
    option: addLabel(value.option),
    condition: addLabel(value.condition),
  }))
}

const {
  visibleFilters,
  validVisibleFilters,
  selectedOptionNames,
  removeFilter,
  setFilterCondition,
  getFilterById,
  setFilterValue,
  replaceOrAddFilter,
  getLastFilter,
  addEmptyFilter,
  setSelectedOptions,
  getFilteredItems,
} = useVisibleFilters()

const { filterOptions, getFilterConditions } = useFilterOptions(
  props.options,
  props.autoqaFields,
  props.spotlightFields,
  props.customCategoryFields,
  props.intelligentFilterFields,
  selectedOptionNames,
  props.modalType,
)
const { getValueComponentProps, loadOptionValues, getValue, getValueType } = useFilterValues()

const filterApplied = (filterId: string, e: FilterItemEmit) => {
  setFilterCondition(filterId, e.selectedCondition)

  const filter = getFilterById(filterId)
  const filterType = getValueType(filter)
  setFilterValue(filterId, filterType, e.value)
}

const addSimilarFilter = async (e: FilterItemEmit, filterOption: FilterOption) => {
  await filterAddingStarted(filterOption)
  const lastFilter = getLastFilter()
  filterApplied(lastFilter.id, e)
}

const filterAddingStarted = async (option: FilterOption) => {
  addEmptyFilter(option)
  await loadOptionValues(option)
}

const debouncedEmit = debounce((val) => {
  emit('update:modelValue', cloneDeep(val))
}, 300)

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue?.length && !isEqual(props.modelValue, validVisibleFilters.value)) {
      const translatedOptions = translateLabels(cloneDeep(props.modelValue))
      setSelectedOptions(translatedOptions)
    }
  },
  { deep: true, immediate: true },
)

watch(validVisibleFilters, (val) => debouncedEmit(val), { deep: true })

watch(
  () => visibleFilters.value,
  (newVal, oldVal) => {
    if (!newVal?.length || oldVal?.length) return

    newVal.filter((row) => row.option.name && row.values).map((row) => loadOptionValues(row.option))
  },
  {
    immediate: true,
  },
)

const helpdeskFilters = computed(() => {
  return props.modelValue.filter(({ option }) => helpdeskGroupFilter(option))
})

const getComponentPropOptionsArray = (componentProps: ValueComponentProps) =>
  componentProps?.groups?.flatMap((item) => item?.options ?? []) ?? []

const remapCustomOptionLabel = (filterOption: AddedFilter): DropdownSelectedValue[] => {
  const filterOptionValue = filterOption.value as DropdownSelectedValue[]
  const optionProps = getComponentPropOptionsArray(filterOption.valueComponentProps)

  return filterOptionValue.map((f) => {
    const match = optionProps.find((o) => o.value === f.value)

    return {
      ...f,
      label: match?.label ?? f.label,
    }
  })
}

const toFilterGroupItem = (d: DeconstructedOption) => {
  const descriptionKey = getDescriptionKey(d.option)
  const hideDescription = d.option.isSpotlight

  const filterOption: AddedFilter = {
    id: d.id,
    label: d.option.label,
    name: d.option.name,
    showAddSimilarButton: !!filterOptions.value.find((o) => o?.name === d.option.name),
    description:
      !hideDescription && translationExists(descriptionKey)
        ? i18n.t(descriptionKey, getDescriptionOptions(d.option))
        : undefined,
    selectedCondition: d.condition,
    conditions: getFilterConditions(d.option),
    value: getValue(d),
    valueType: getValueType(d),
    valueComponentProps: getValueComponentProps(d) as ValueComponentProps,
    intelligenceIcon:
      hasIntelligenceIcon.includes(d.option.name) ||
      d.option.isAutoQa ||
      d.option.isSpotlight ||
      d.option.isCustomCategory,
    botIcon: hasBotIcon(d.option),
    status: d.option.status,
    isSpotlight: d.option.isSpotlight,
    disabled: isDowngradedFilter(d.option) || forcedFilterOption(d.option, props.modalType),
    forceHideRemoveButton: forcedFilterOption(d.option, props.modalType),
  }

  const isGenericCustomCategoryOption = d.option.isCustomCategory && filterOption.valueType === FilterValueType.Generic
  const isDropdownValueArray =
    Array.isArray(filterOption.value) && filterOption.value.every((v) => typeof v === 'object' && 'value' in v)

  if (isGenericCustomCategoryOption && isDropdownValueArray) {
    filterOption.value = remapCustomOptionLabel(filterOption)
  }

  return filterOption
}

const getFilterGroupItems = (filterFn: GroupFilter) => getFilteredItems(filterFn).map(toFilterGroupItem)

const getActiveFilterCount = (groupFilter: GroupFilter) => {
  const groupItems = getFilterGroupItems(groupFilter)
  const activeFilters = groupItems.filter((v) => isActiveFilter(v.value, v.selectedCondition))
  return activeFilters.length
}

const hasActiveVisibleFilters = (filterFn: GroupFilter) => getFilteredItems(filterFn).some((filter) => isActiveFilter(filter.values, filter.condition))

watch(
  () => getActiveFilterCount(dateGroupFilter),
  (count) => {
    emit('disable-selecting-tickets', !!count)
  },
)
</script>

<style lang="scss" src="@/styles/calendar.scss"></style>
